import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, Modal, TextInput, ScrollView, Image, Platform, Animated } from 'react-native';
import { getFirestore, doc, getDoc, setDoc, updateDoc, query, where, collection, getDocs, limit } from "firebase/firestore";
import { Ionicons } from '@expo/vector-icons';
import genericProfilePicture from '../../assets/generic.jpg';
import reminderIntegration from '../../services/ReminderIntegration';

const ADMIN_EMAIL_WHITELIST = [
    'trevorhawkins@dsignable.com',
    'info@mytassel.com',
];

const EventManagement = ({
    rsvpData,
    rsvpUsers,
    sharedContacts,
    reminderSettings,
    setReminderSettings,
    markReminderSettingsSaved,
    currentUser,
    loadDashboardData,
    formatDate,
    cardStyle,
    isMobile,
    width,
    isAdminOwnerPreview = false
}) => {
    const [selectedTab, setSelectedTab] = useState('going');
    const [loading, setLoading] = useState(false);
    const [userDetailsCache, setUserDetailsCache] = useState({});
    
    // New state for managing multiple events
    const [timelineEvents, setTimelineEvents] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [eventRsvpMap, setEventRsvpMap] = useState({});
    
    // Event editing state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    
    // Animation values
    const fadeAnim = useRef(new Animated.Value(0)).current;
    const slideAnim = useRef(new Animated.Value(20)).current;
    const scaleAnim = useRef(new Animated.Value(0.95)).current;
    const addButtonScale = useRef(new Animated.Value(1)).current;
    const editButtonScale = useRef(new Animated.Value(1)).current;
    const saveButtonScale = useRef(new Animated.Value(1)).current;
    
    // Hover states
    const [addButtonHovered, setAddButtonHovered] = useState(false);
    const [editButtonHovered, setEditButtonHovered] = useState(false);
    const [saveButtonHovered, setSaveButtonHovered] = useState(false);
    const [saveSettingsHovered, setSaveSettingsHovered] = useState(false);

    // Dynamic reminder options from Firestore
    const [dynamicReminderOptions, setDynamicReminderOptions] = useState(null);
    
    // Entrance animations
    useEffect(() => {
        Animated.parallel([
            Animated.timing(fadeAnim, {
                toValue: 1,
                duration: 400,
                useNativeDriver: true
            }),
            Animated.timing(slideAnim, {
                toValue: 0,
                duration: 400,
                useNativeDriver: true
            }),
            Animated.spring(scaleAnim, {
                toValue: 1,
                friction: 8,
                tension: 40,
                useNativeDriver: true
            })
        ]).start();
    }, []);
    
    useEffect(() => {
        const fetchReminderOptions = async () => {
            try {
                const db = getFirestore();
                const snap = await getDoc(doc(db, 'config', 'reminderSettings'));
                if (snap.exists() && snap.data().defaultOptions?.length) {
                    setDynamicReminderOptions(snap.data().defaultOptions.map(opt => ({
                        value: opt.value,
                        label: opt.label,
                        shortLabel: opt.label.replace('Day of Event + ', '').replace(' Before', '') + ' + Day Of',
                    })));
                }
            } catch (err) {
                console.warn('Using fallback reminder options:', err.message);
            }
        };
        fetchReminderOptions();
    }, []);

    // Button hover animation helper
    const handleButtonHover = (animValue, isEntering) => {
        Animated.spring(animValue, {
            toValue: isEntering ? 1.05 : 1,
            friction: 8,
            useNativeDriver: true
        }).start();
    };
    
    // Button animation helpers
    const animateButtonPress = (animValue, callback) => {
        Animated.sequence([
            Animated.spring(animValue, {
                toValue: 0.92,
                friction: 8,
                useNativeDriver: true
            }),
            Animated.spring(animValue, {
                toValue: 1,
                friction: 8,
                useNativeDriver: true
            })
        ]).start(() => callback && callback());
    };
    
    // Get currently selected event details
    const selectedEvent = timelineEvents.find(e => e.id === selectedEventId) || timelineEvents[0];
    
    // Get RSVP data for selected event
    const currentEventRsvp = selectedEventId && eventRsvpMap[selectedEventId] 
        ? eventRsvpMap[selectedEventId] 
        : { Going: [], Maybe: [], Decline: [], rsvpUsers: [] };

    // Calculate RSVP statistics for currently selected event
    // Note: sharedContacts (invited) is GLOBAL across all events
    const rsvpStats = {
        going: currentEventRsvp.Going?.length || 0,
        maybe: currentEventRsvp.Maybe?.length || 0,
        decline: currentEventRsvp.Decline?.length || 0,
        invited: sharedContacts?.length || 0, // Global shared contacts
        total: (currentEventRsvp.Going?.length || 0) + (currentEventRsvp.Maybe?.length || 0) + (currentEventRsvp.Decline?.length || 0)
    };

    // Load all Timeline events and map them to their RSVP data
    useEffect(() => {
        const loadAllEvents = async () => {
            if (!currentUser?.email) return;
            
            try {
                const db = getFirestore();
                const userDocRef = doc(db, "users", currentUser.email);
                const userDoc = await getDoc(userDocRef);
                
                if (!userDoc.exists()) {
                    
                    return;
                }
                
                const userData = userDoc.data();
                const eventsPageConfig = userData.pages?.eventsPageConfig;
                const cards = eventsPageConfig?.cards || [];
                
                // Find all Event cards (instead of Timeline events)
                const eventCards = cards.filter(card => card.type === 'Event');
                const allEvents = eventCards.map(card => ({
                    id: card.id,
                    title: card.eventTitle || card.title || '',
                    date: card.eventDate || card.date || '',
                    time: card.time || '12:00', // Add time if it exists, otherwise default
                    endDate: card.eventEndDate || card.endDate || '',
                    location: card.eventLocation || card.location || '',
                    description: card.eventDescription || card.description || '',
                    locationCoords: card.locationCoords || null,
                    cardId: card.id, // Keep reference to the card
                    rsvpEnabled: card.rsvpEnabled,
                    reminderSettings: card.reminderSettings,
                    eventPhotos: card.eventPhotos || []
                }));
                
                if (allEvents.length === 0) {
                    
                    setTimelineEvents([]);
                    return;
                }
                
                
                setTimelineEvents(allEvents);
                
                // If no event selected yet, select the first one
                if (!selectedEventId && allEvents.length > 0) {
                    setSelectedEventId(allEvents[0].id);
                }
                
                // Map each event to its RSVP data (now stored in the Event card itself)
                const rsvpMap = {};
                
                allEvents.forEach(event => {
                    // Find the corresponding card for RSVP data
                    const eventCard = eventCards.find(card => card.id === event.cardId);
                    const eventRsvpData = {
                        Going: eventCard?.going || [],
                        Maybe: eventCard?.maybe || [],
                        Decline: eventCard?.decline || [],
                        rsvpUsers: []
                    };
                    
                    // Process RSVP users with status
                    ['Going', 'Maybe', 'Decline'].forEach(status => {
                        const usersInStatus = eventRsvpData[status] || [];
                        eventRsvpData.rsvpUsers.push(
                            ...usersInStatus.map(user => {
                                // Handle email strings vs user objects
                                    if (typeof user === 'string') {
                                        return { email: user, status: status };
                                    }
                                    return { ...user, status: status };
                                })
                            );
                        });
                        
                        rsvpMap[event.id] = eventRsvpData;
                });
                
                setEventRsvpMap(rsvpMap);
                
            } catch (error) {
                
            }
        };
        
        loadAllEvents();
    }, [currentUser?.email, selectedEventId]);

    // Enhanced function to get user details from cache or fetch from Firestore
    const fetchUserDetails = async (user) => {
        const userKey = user.uid || user.email || user.id;
        if (!userKey || userDetailsCache[userKey]) return userDetailsCache[userKey] || user;

        try {
            const db = getFirestore();
            let userDoc = null;
            
            // Try to find user by email first (most common case)
            if (user.email) {
                userDoc = await getDoc(doc(db, 'users', user.email.toLowerCase()));
            }
            
            // If not found by email and we have a UID, try by UID
            if (!userDoc?.exists() && user.uid) {
                const usersQuery = query(
                    collection(db, 'users'),
                    where('uid', '==', user.uid),
                    limit(1)
                );
                const querySnapshot = await getDocs(usersQuery);
                if (!querySnapshot.empty) {
                    userDoc = querySnapshot.docs[0];
                }
            }
            
            if (userDoc?.exists()) {
                const userData = userDoc.data();
                const fullUserData = {
                    ...user,
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    profilePictureURL: userData.profilePictureURL,
                    name: userData.firstName && userData.lastName 
                        ? `${userData.firstName} ${userData.lastName}`
                        : userData.firstName || userData.lastName || user.email
                };
                
                // Cache the result
                setUserDetailsCache(prev => ({
                    ...prev,
                    [userKey]: fullUserData
                }));
                
                return fullUserData;
            }
        } catch (error) {
            
        }
        
        return user;
    };

    // Process RSVP data - convert UIDs to user objects when needed (fallback if rsvpUsers not provided)
    const processRsvpUsers = (rsvpArray) => {
        if (!rsvpArray || !Array.isArray(rsvpArray)) return [];
        
        return rsvpArray.map(item => {
            // If item is already an object with user info, return as-is
            if (typeof item === 'object' && item !== null) {
                return item;
            }
            // If item is a UID string, create a minimal user object
            if (typeof item === 'string') {
                return {
                    uid: item,
                    name: 'User',
                    email: item.includes('@') ? item : '',
                    timestamp: new Date().toISOString()
                };
            }
            return item;
        });
    };

    // Group users by RSVP status for currently selected event
    // Note: 'invited' uses global sharedContacts, not per-event data
    const usersByStatus = {
        going: currentEventRsvp.rsvpUsers?.filter(user => user.status === 'Going') || processRsvpUsers(currentEventRsvp?.Going),
        maybe: currentEventRsvp.rsvpUsers?.filter(user => user.status === 'Maybe') || processRsvpUsers(currentEventRsvp?.Maybe),
        decline: currentEventRsvp.rsvpUsers?.filter(user => user.status === 'Decline') || processRsvpUsers(currentEventRsvp?.Decline),
        invited: sharedContacts || [] // Global across all events
    };

    // Global reminder options that sync with RSVP widget
    const fallbackReminderOptions = [
        { value: 'days_before_10', label: 'Day of Event + 10 Days Before', shortLabel: '10 Days + Day Of' },
        { value: 'days_before_7', label: 'Day of Event + 7 Days Before', shortLabel: '7 Days + Day Of' },
        { value: 'days_before_3', label: 'Day of Event + 3 Days Before', shortLabel: '3 Days + Day Of' },
        { value: 'days_before_1', label: 'Day of Event + 1 Day Before', shortLabel: '1 Day + Day Of' }
    ];
    const globalReminderOptions = dynamicReminderOptions || fallbackReminderOptions;

    const isAdminUser = ADMIN_EMAIL_WHITELIST.includes(currentUser?.email?.toLowerCase());

    const testReminderOptions = [
        { value: 'test_5min', label: 'TEST: Send in 5 minutes', testDelay: 5 },
        { value: 'test_10min', label: 'TEST: Send in 10 minutes', testDelay: 10 },
    ];

    const scheduleTestReminder = async (testDelay) => {
        try {
            const result = await reminderIntegration.callAPI('/schedule-event-reminders', {
                eventOwnerId: currentUser.email.toLowerCase(),
                testMode: true,
                testDelay,
            });
            if (result?.success) {
                alert(`Test reminder scheduled! It will fire in ${testDelay} minutes.`);
            } else {
                alert(`Test reminder failed: ${result?.reason || result?.error || 'Unknown error'}`);
            }
        } catch (error) {
            alert(`Failed to schedule test reminder: ${error.message}`);
        }
    };



    // Handle global reminder toggle - sync with RSVP widget
    const handleGlobalReminderToggle = (reminderTime) => {
        const currentTimes = reminderSettings.reminderTimes || [];
        const isCurrentlySelected = currentTimes.includes('day_of') && currentTimes.includes(reminderTime);
        
        let newReminderTimes;
        if (isCurrentlySelected) {
            // Remove this reminder option (remove both day_of and the specific time)
            newReminderTimes = currentTimes.filter(t => t !== reminderTime && t !== 'day_of');
        } else {
            // Add this reminder option (ensure we have day_of + the specific time, no duplicates)
            newReminderTimes = currentTimes.filter(t => t !== 'day_of' && !t.startsWith('days_before_'));
            newReminderTimes.push('day_of', reminderTime);
        }
        
        // Remove any duplicate entries
        newReminderTimes = [...new Set(newReminderTimes)];
        
        const newSettings = {
            enabled: newReminderTimes.length > 0,
            reminderTimes: newReminderTimes
        };
        
        setReminderSettings(newSettings);
    };

    // Save reminder settings using the parent's refresh function
    const saveReminderSettings = async () => {
        if (isAdminOwnerPreview) { alert('Preview mode: changes are not saved.'); return; }
        try {
            // Validate that we have reminder times to save
            if (!reminderSettings.reminderTimes || reminderSettings.reminderTimes.length === 0) {
                alert("Please select at least one reminder option before saving.");
                return;
            }

            const db = getFirestore();
            const userEmail = currentUser.email; // Use email as document ID like RSVP widget does
            if (!userEmail) {
                alert("Unable to save: User email not found.");
                return;
            }
            
            const userDocRef = doc(db, "users", userEmail);
            
            
            
            
            // First, get the current document to preserve existing data
            const currentDoc = await getDoc(userDocRef);
            let currentData = {};
            if (currentDoc.exists()) {
                currentData = currentDoc.data();
            }
            
            // Prepare the update data with proper nesting
            const updateData = {
                ...currentData,
                pages: {
                    ...currentData.pages,
                    eventsPageConfig: {
                        ...currentData.pages?.eventsPageConfig,
                        reminderSettings: {
                            enabled: reminderSettings.reminderTimes.length > 0,
                            reminderTimes: reminderSettings.reminderTimes,
                            lastUpdated: new Date().toISOString()
                        }
                    }
                }
            };
            
            
            
            // Use setDoc to completely replace the document with merged data
            await setDoc(userDocRef, updateData);
            
            
            
            // Mark settings as saved to prevent auto-refresh from overwriting
            markReminderSettingsSaved();
            
            // RESCHEDULE existing reminders with new settings instead of just scheduling new ones
            try {
                // Extract event data for rescheduling
                const eventsPageConfig = updateData.pages?.eventsPageConfig;
                const eventsCards = eventsPageConfig?.cards || [];
                
                let eventData = {
                    title: 'Graduation Celebration',
                    date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                    location: '',
                    description: 'Join us for this special celebration!'
                };
                
                // Find RSVP card with event details
                eventsCards.forEach(card => {
                    if (card.type === 'RSVP') {
                        if (card.eventTitle) eventData.title = card.eventTitle;
                        if (card.eventDate) eventData.date = card.eventDate;
                        if (card.eventLocation) eventData.location = card.eventLocation;
                        if (card.eventDescription) eventData.description = card.eventDescription;
                    }
                });
                
                // Use reschedule API to cancel old reminders and create new ones with updated settings
                const API_BASE_URL = 'https://us-central1-my-tassel.cloudfunctions.net/api';
                const response = await fetch(`${API_BASE_URL}/reschedule-event-reminders`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        eventOwnerId: userEmail,
                        updatedEventData: eventData
                    })
                });

                const result = await response.json();
                
                if (!response.ok) {
                    throw new Error(result.error || `HTTP ${response.status}`);
                }

                
                alert(`Reminder settings saved and reminders updated! Scheduled ${result.rescheduled} reminders for ${result.recipientCount} recipients.`);
                
            } catch (scheduleError) {
                
                alert("Reminder settings saved successfully! Note: Automatic reminder rescheduling failed - you may need to update event details to apply new settings.");
            }
            
            // Refresh dashboard data to sync changes
            setTimeout(() => {
                loadDashboardData();
            }, 500);
        } catch (error) {
            
            console.error("Error details:", {
                message: error.message,
                code: error.code,
                stack: error.stack
            });
            alert(`Failed to save reminder settings: ${error.message || 'Please try again.'}`);
        }
    };

    // Schedule event reminders automatically based on Timeline event via RSVP link
    const scheduleEventReminders = async (userEmail, userData) => {
        
        
        try {
            const eventsPageConfig = userData.pages?.eventsPageConfig;
            const eventsCards = eventsPageConfig?.cards || [];
            
            // Find RSVP card with Timeline link
            const rsvpCard = eventsCards.find(card => card.type === 'RSVP');
            
            if (!rsvpCard?.timelineEventId) {
                
                return;
            }
            
            // Get Timeline event
            const timelineCard = eventsCards.find(card => card.type === 'Timeline');
            if (!timelineCard?.events) {
                
                return;
            }
            
            const timelineEvent = timelineCard.events.find(e => e.id === rsvpCard.timelineEventId);
            if (!timelineEvent) {
                
                return;
            }
            
            const eventData = {
                title: timelineEvent.title,
                date: timelineEvent.datetime,
                location: timelineEvent.location || '',
                description: timelineEvent.description || ''
            };
            
            
            
            const API_BASE_URL = 'https://us-central1-my-tassel.cloudfunctions.net/api';
            
            const response = await fetch(`${API_BASE_URL}/schedule-event-reminders`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    eventOwnerId: userEmail,
                    eventData: {
                        title: eventData.title,
                        eventDate: eventData.date,
                        location: eventData.location,
                        description: eventData.description
                    },
                    testMode: false,
                    testDelay: 0
                })
            });

            const result = await response.json();
            
            if (!response.ok) {
                throw new Error(result.error || `HTTP ${response.status}`);
            }

            
            return result;
            
        } catch (error) {
            
            throw error;
        }
    };

    // Synchronous version of formatUserName for render
    const formatUserNameSync = (user) => {
        if (!user) return 'Anonymous User';
        
        const userKey = user.uid || user.email || user.id;
        
        // Check if this is the current user (event owner)
        if (currentUser && (
            user.email === currentUser.email || 
            user.uid === currentUser.uid ||
            userKey === currentUser.email ||
            userKey === currentUser.uid
        )) {
            // Return current user's name
            if (currentUser.firstName && currentUser.lastName) {
                return `${currentUser.firstName} ${currentUser.lastName}`;
            }
            if (currentUser.firstName) return currentUser.firstName;
            if (currentUser.name) return currentUser.name;
            return currentUser.email || 'You';
        }
        
        // Use cached data if available
        if (userKey && userDetailsCache[userKey]) {
            const cachedUser = userDetailsCache[userKey];
            if (cachedUser.firstName && cachedUser.lastName) {
                return `${cachedUser.firstName} ${cachedUser.lastName}`;
            }
            return cachedUser.name || cachedUser.firstName || cachedUser.lastName;
        }
        
        // Check for displayName field (common in Firebase Auth)
        if (user.displayName) {
            return user.displayName;
        }
        
        // Fallback to existing user data - check all possible name fields
        if (user.name && typeof user.name === 'string' && user.name.trim()) {
            return user.name;
        } else if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        } else if (user.firstName) {
            return user.firstName;
        } else if (user.lastName) {
            return user.lastName;
        }
        
        // Trigger async fetch for better data (don't await to avoid blocking UI)
        if (userKey && (user.uid || user.email)) {
            fetchUserDetails(user).catch(console.error);
        }
        
        // Last resort - show email or fallback
        if (user.email && user.email.trim()) {
            return user.email;
        }
        if (userKey && userKey.includes('@')) {
            return userKey;
        }
        
        return 'Anonymous User';
    };

    // Get profile picture URL with cache support
    const getUserProfilePicture = (user) => {
        if (!user) return require('../../assets/generic.jpg');
        
        // Check if this is the current user
        if (currentUser && (
            user.email === currentUser.email || 
            user.uid === currentUser.uid
        )) {
            if (currentUser.profilePictureURL) {
                return { uri: currentUser.profilePictureURL };
            }
        }
        
        const userKey = user.uid || user.email || user.id;
        if (userKey && userDetailsCache[userKey]?.profilePictureURL) {
            return { uri: userDetailsCache[userKey].profilePictureURL };
        }
        if (user.profilePictureURL) {
            return { uri: user.profilePictureURL };
        }
        return require('../../assets/generic.jpg');
    };

    // Helper function to get user display info for shared contacts
    const formatContactInfo = (contact) => {
        let name = formatUserNameSync(contact);
        const hasRealName = name !== 'Anonymous User' && name !== 'User';
        const contactMethod = contact.email || contact.phone || '';
        // For phone-only contacts, use the phone as the display name
        if (!hasRealName && contact.phone) {
            name = contact.phone;
        }
        const channelLabel = contact.source === 'sms_share' ? 'SMS' : contact.source === 'email_share' ? 'Email' : null;
        return { name, contactMethod: hasRealName ? contactMethod : '', channelLabel };
    };

    // Tab configuration
    const tabConfig = [
        { 
            key: 'going', 
            label: 'Going', 
            count: rsvpStats.going, 
            color: '#28a745', 
            bgColor: '#d4edda',
            iconName: 'checkmark-circle'
        },
        { 
            key: 'maybe', 
            label: 'Maybe', 
            count: rsvpStats.maybe, 
            color: '#856404', 
            bgColor: '#fff3cd',
            iconName: 'help-circle'
        },
        { 
            key: 'decline', 
            label: 'Not Going', 
            count: rsvpStats.decline, 
            color: '#721c24', 
            bgColor: '#f8d7da',
            iconName: 'close-circle'
        },
        { 
            key: 'invited', 
            label: 'Invited', 
            count: rsvpStats.invited, 
            color: '#e65100', 
            bgColor: '#fff3e0',
            iconName: 'mail'
        }
    ];

    const colors = {
        primary: '#4c956c',
        info: '#3b82f6',
        text: '#1a3a2a',
        textMuted: '#5a6b63',
        white: '#ffffff',
        danger: '#dc3545',
        cream: '#fefde2',
        creamLight: 'rgba(254, 253, 226, 0.95)',
        border: 'rgba(76, 149, 108, 0.15)',
    };

    // Open edit modal for an event
    const handleEditEvent = (event = null) => {
        if (event) {
            // Edit specific event
            setEditingEvent({
                ...event,
                cardId: event.cardId,
                date: event.date || new Date().toISOString().split('T')[0],
                time: event.time || '12:00'
            });
        } else if (selectedEvent) {
            // Edit currently selected event
            setEditingEvent({
                ...selectedEvent,
                cardId: selectedEvent.cardId,
                date: selectedEvent.date || new Date().toISOString().split('T')[0],
                time: selectedEvent.time || '12:00'
            });
        }
        setShowEditModal(true);
    };

    // Add new event
    const handleAddEvent = () => {
        const newEvent = {
            id: `event-${Date.now()}`,
            title: '',
            description: '',
            location: '',
            date: new Date().toISOString().split('T')[0],
            time: '12:00',
            datetime: new Date().toISOString(),
            isNew: true
        };
        setEditingEvent(newEvent);
        setShowEditModal(true);
    };

    // Save event changes to Firestore
    const handleSaveEvent = async () => {
        if (isAdminOwnerPreview) { alert('Preview mode: changes are not saved.'); setShowEditModal(false); return; }
        if (!editingEvent || !currentUser?.email) return;
        
        setIsSaving(true);
        try {
            const db = getFirestore();
            const userDocRef = doc(db, "users", currentUser.email);
            const userDoc = await getDoc(userDocRef);
            
            if (!userDoc.exists()) {
                alert("User document not found");
                return;
            }
            
            const userData = userDoc.data();
            const eventsPageConfig = userData.pages?.eventsPageConfig || {};
            const cards = eventsPageConfig.cards || [];
            
            // Find the Event card to update
            const eventCardIndex = cards.findIndex(card => card.id === editingEvent.cardId);
            
            // Prepare the updated event details
            const updatedEventData = {
                eventTitle: updatedEventDetails.title,
                eventDate: updatedEventDetails.date,
                eventEndDate: updatedEventDetails.endDate || null,
                eventLocation: updatedEventDetails.location,
                eventDescription: updatedEventDetails.description,
                locationCoords: updatedEventDetails.locationCoords || null,
                time: updatedEventDetails.time || '12:00'
            };
            
            let updatedCards;
            
            if (editingEvent.isNew) {
                // Create new Event card
                const newEventCard = {
                    type: 'Event',
                    id: `event-${Date.now()}`,
                    ...updatedEventData,
                    rsvpEnabled: true,
                    reminderSettings: {},
                    eventPhotos: []
                };
                updatedCards = [...cards, newEventCard];
                
                // Update local state
                setTimelineEvents(prev => [...prev, { 
                    id: newEventCard.id,
                    title: updatedEventData.eventTitle,
                    date: updatedEventData.eventDate,
                    time: updatedEventData.time,
                    endDate: updatedEventData.eventEndDate,
                    location: updatedEventData.eventLocation,
                    description: updatedEventData.eventDescription,
                    locationCoords: updatedEventData.locationCoords,
                    cardId: newEventCard.id,
                    rsvpEnabled: true,
                    reminderSettings: {},
                    eventPhotos: []
                }]);
            } else {
                // Update existing Event card
                const eventCardIndex = cards.findIndex(card => card.id === editingEvent.cardId);
                
                if (eventCardIndex >= 0) {
                    updatedCards = cards.map((card, idx) => 
                        idx === eventCardIndex 
                            ? { ...card, ...updatedEventData }
                            : card
                    );
                    
                    // Update local state
                    setTimelineEvents(prev => prev.map(e => 
                        e.id === editingEvent.id ? { 
                            ...e,
                            title: updatedEventData.eventTitle,
                            date: updatedEventData.eventDate,
                            time: updatedEventData.time,
                            endDate: updatedEventData.eventEndDate,
                            location: updatedEventData.eventLocation,
                            description: updatedEventData.eventDescription,
                            locationCoords: updatedEventData.locationCoords
                        } : e
                    ));
                } else {
                    // This shouldn't happen for existing events
                    console.error('Event card not found for editing');
                    return;
                }
            }
            
            // Save to Firestore
            await updateDoc(userDocRef, {
                'pages.eventsPageConfig.cards': updatedCards
            });
            
            setShowEditModal(false);
            setEditingEvent(null);
            
            // Refresh dashboard data
            if (loadDashboardData) {
                setTimeout(() => loadDashboardData(), 500);
            }
            
            alert('Event saved successfully!');
        } catch (error) {
            console.error('Error saving event:', error);
            alert('Failed to save event. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    // Delete event
    const handleDeleteEvent = async () => {
        if (!editingEvent || !currentUser?.email || editingEvent.isNew) {
            setShowEditModal(false);
            setEditingEvent(null);
            return;
        }
        
        if (!confirm('Are you sure you want to delete this event?')) return;
        
        setIsSaving(true);
        try {
            const db = getFirestore();
            const userDocRef = doc(db, "users", currentUser.email);
            const userDoc = await getDoc(userDocRef);
            
            if (!userDoc.exists()) return;
            
            const userData = userDoc.data();
            const eventsPageConfig = userData.pages?.eventsPageConfig || {};
            const cards = eventsPageConfig.cards || [];
            
            // Find and remove the Event card
            const updatedCards = cards.filter(card => card.id !== editingEvent.cardId);
            
            await updateDoc(userDocRef, {
                'pages.eventsPageConfig.cards': updatedCards
            });
            
            // Update local state
            setTimelineEvents(prev => prev.filter(e => e.id !== editingEvent.id));
            setShowEditModal(false);
            setEditingEvent(null);
            
            if (loadDashboardData) {
                setTimeout(() => loadDashboardData(), 500);
            }
            
            alert('Event deleted successfully!');
        } catch (error) {
            console.error('Error deleting event:', error);
            alert('Failed to delete event. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Animated.View style={[
            cardStyle, 
            { 
                padding: isMobile ? 16 : 20, 
                overflow: 'hidden', 
                position: 'relative',
                opacity: fadeAnim,
                transform: [
                    { translateY: slideAnim },
                    { scale: scaleAnim }
                ]
            }
        ]}>
            {/* Decorative Bubbles */}
            <Animated.View style={{
                position: 'absolute',
                top: -20,
                right: 40,
                width: 60,
                height: 60,
                borderRadius: 30,
                backgroundColor: 'rgba(59, 130, 246, 0.05)',
                opacity: fadeAnim
            }} />
            <Animated.View style={{
                position: 'absolute',
                bottom: 20,
                left: -15,
                width: 40,
                height: 40,
                borderRadius: 20,
                backgroundColor: 'rgba(59, 130, 246, 0.04)',
                opacity: fadeAnim
            }} />
            
            {/* Header */}
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <View>
                    <Text style={{
                        fontSize: isMobile ? 18 : 22,
                        fontWeight: '700',
                        color: colors.text,
                        fontFamily: 'Bodoni72Smallcaps'
                    }}>
                        Event Management
                    </Text>
                    <Text style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
                        RSVPs & reminders
                    </Text>
                </View>
                
                {/* Event Count Badge */}
                {timelineEvents.length > 0 && (
                    <View style={{
                        backgroundColor: colors.info,
                        paddingHorizontal: 12,
                        paddingVertical: 6,
                        borderRadius: 16
                    }}>
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#fff' }}>
                            {timelineEvents.length}
                        </Text>
                    </View>
                )}
            </View>
                
            {/* Timeline Events Selector */}
            <View style={{ marginBottom: 16 }}>
            {timelineEvents.length > 0 ? (
                    <View>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <Text style={{
                                fontSize: 12,
                                color: colors.textMuted,
                                fontWeight: '500'
                            }}>
                                Select event ({timelineEvents.length})
                            </Text>
                            <View style={{ flexDirection: 'row', gap: 10 }}>
                                <Animated.View style={{ transform: [{ scale: addButtonScale }] }}>
                                    <TouchableOpacity
                                        onPress={() => animateButtonPress(addButtonScale, handleAddEvent)}
                                        onMouseEnter={() => {
                                            setAddButtonHovered(true);
                                            handleButtonHover(addButtonScale, true);
                                        }}
                                        onMouseLeave={() => {
                                            setAddButtonHovered(false);
                                            handleButtonHover(addButtonScale, false);
                                        }}
                                        activeOpacity={0.9}
                                        style={{
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            backgroundColor: addButtonHovered ? '#3d7a58' : colors.primary,
                                            paddingVertical: 10,
                                            paddingHorizontal: 16,
                                            borderRadius: 12,
                                            gap: 6,
                                            shadowColor: colors.primary,
                                            shadowOffset: { width: 0, height: addButtonHovered ? 6 : 4 },
                                            shadowOpacity: addButtonHovered ? 0.4 : 0.3,
                                            shadowRadius: addButtonHovered ? 10 : 6,
                                            elevation: addButtonHovered ? 6 : 4,
                                            ...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {})
                                        }}
                                    >
                                        <Ionicons name="add-circle" size={18} color="#fff" />
                                        <Text style={{ fontSize: 14, color: '#fff', fontWeight: '600' }}>Add</Text>
                                    </TouchableOpacity>
                                </Animated.View>
                                <Animated.View style={{ transform: [{ scale: editButtonScale }] }}>
                                    <TouchableOpacity
                                        onPress={() => animateButtonPress(editButtonScale, () => handleEditEvent())}
                                        onMouseEnter={() => {
                                            if (selectedEvent) {
                                                setEditButtonHovered(true);
                                                handleButtonHover(editButtonScale, true);
                                            }
                                        }}
                                        onMouseLeave={() => {
                                            setEditButtonHovered(false);
                                            handleButtonHover(editButtonScale, false);
                                        }}
                                        activeOpacity={0.9}
                                        style={{
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            backgroundColor: !selectedEvent ? '#ccc' : editButtonHovered ? '#4a8fd9' : colors.info,
                                            paddingVertical: 10,
                                            paddingHorizontal: 16,
                                            borderRadius: 12,
                                            gap: 6,
                                            shadowColor: colors.info,
                                            shadowOffset: { width: 0, height: editButtonHovered ? 6 : 4 },
                                            shadowOpacity: selectedEvent ? (editButtonHovered ? 0.4 : 0.3) : 0,
                                            shadowRadius: editButtonHovered ? 10 : 6,
                                            elevation: selectedEvent ? (editButtonHovered ? 6 : 4) : 0,
                                            ...(Platform.OS === 'web' ? { cursor: selectedEvent ? 'pointer' : 'not-allowed', transition: 'all 0.2s ease' } : {})
                                        }}
                                        disabled={!selectedEvent}
                                    >
                                        <Ionicons name="create" size={18} color="#fff" />
                                        <Text style={{ fontSize: 14, color: '#fff', fontWeight: '600' }}>Edit</Text>
                                    </TouchableOpacity>
                                </Animated.View>
                            </View>
                        </View>
                        
                        {/* Event Cards */}
                        <ScrollView 
                            horizontal={!isMobile}
                            showsHorizontalScrollIndicator={false}
                            style={{ marginBottom: 12 }}
                        >
                            <View style={{
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: 12
                            }}>
                                {timelineEvents.map((event) => {
                                    const isSelected = event.id === selectedEventId;
                                    const hasRsvp = eventRsvpMap[event.id] && (
                                        eventRsvpMap[event.id].Going.length > 0 ||
                                        eventRsvpMap[event.id].Maybe.length > 0 ||
                                        eventRsvpMap[event.id].Decline.length > 0
                                    );
                                    
                                    return (
                                        <TouchableOpacity
                                            key={event.id}
                                            style={{
                                                backgroundColor: isSelected ? 'rgba(76, 149, 108, 0.1)' : 'rgba(0,0,0,0.02)',
                                                borderWidth: 2,
                                                borderColor: isSelected ? colors.primary : 'rgba(0,0,0,0.08)',
                                                borderRadius: 14,
                                                padding: 16,
                                                minWidth: isMobile ? '100%' : 240
                                            }}
                                            onPress={() => setSelectedEventId(event.id)}
                                        >
                                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                                <Text style={{
                                                    fontSize: 16,
                                                    fontWeight: '700',
                                                    color: isSelected ? colors.text : colors.textMuted,
                                                    flex: 1
                                                }} numberOfLines={1}>
                                                    {event.title}
                                                </Text>
                                                {hasRsvp && (
                                                    <View style={{
                                                        backgroundColor: colors.primary,
                                                        paddingHorizontal: 8,
                                                        paddingVertical: 3,
                                                        borderRadius: 8
                                                    }}>
                                                        <Text style={{ fontSize: 10, color: 'white', fontWeight: 'bold' }}>
                                                            RSVP
                                                        </Text>
                                                    </View>
                                                )}
                                            </View>
                                            
                                            {event.date && (
                                                <Text style={{ 
                                                    fontSize: 14, 
                                                    color: colors.textMuted,
                                                    marginBottom: hasRsvp ? 8 : 0
                                                }}>
                                                    {new Date(event.date).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric'
                                                    })}
                                                    {event.time && event.time !== '12:00' ? ` at ${event.time}` : ''}
                                                </Text>
                                            )}
                                            
                                            {hasRsvp && (
                                                <View style={{ 
                                                    flexDirection: 'row', 
                                                    gap: 12, 
                                                    marginTop: 4
                                                }}>
                                                    <Text style={{ fontSize: 13, color: '#28a745', fontWeight: '700' }}>
                                                        ✓ {eventRsvpMap[event.id].Going.length}
                                                    </Text>
                                                    <Text style={{ fontSize: 13, color: '#856404', fontWeight: '700' }}>
                                                        ? {eventRsvpMap[event.id].Maybe.length}
                                                    </Text>
                                                    <Text style={{ fontSize: 13, color: '#721c24', fontWeight: '700' }}>
                                                        ✕ {eventRsvpMap[event.id].Decline.length}
                                                    </Text>
                                                </View>
                                            )}
                                        </TouchableOpacity>
                                    );
                                })}
                            </View>
                        </ScrollView>
                    </View>
                ) : (
                    <View style={{
                        backgroundColor: 'rgba(0,0,0,0.03)',
                        padding: 12,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: 'rgba(0,0,0,0.06)',
                        borderStyle: 'dashed'
                    }}>
                        <Text style={{
                            fontSize: 12,
                            color: colors.textMuted,
                            textAlign: 'center'
                        }}>
                            No Timeline events found
                        </Text>
                    </View>
                )}
            </View>

            {/* RSVP Status Tabs - Only show if event is selected */}
            {selectedEvent && (
                <View style={{ marginBottom: 16 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <Text style={{
                            fontSize: 14,
                            fontWeight: '600',
                            color: colors.text
                        }}>
                            RSVPs: {selectedEvent.title}
                        </Text>
                        <Text style={{
                            fontSize: 11,
                            color: colors.textMuted
                        }}>
                            {rsvpStats.total + rsvpStats.invited} contacts
                        </Text>
                    </View>
                
                {/* Tab Navigation - Compact */}
                <View style={{
                    flexDirection: 'row',
                    gap: 8,
                    marginBottom: 12
                }}>
                    {tabConfig.map((tab) => (
                        <TouchableOpacity
                            key={tab.key}
                            style={{
                                flex: 1,
                                backgroundColor: selectedTab === tab.key ? tab.bgColor : 'rgba(0,0,0,0.03)',
                                borderColor: selectedTab === tab.key ? tab.color : 'rgba(0,0,0,0.08)',
                                borderWidth: 1.5,
                                paddingVertical: 10,
                                paddingHorizontal: 8,
                                borderRadius: 10,
                                alignItems: 'center'
                            }}
                            onPress={() => setSelectedTab(tab.key)}
                        >
                            <Text style={{
                                fontSize: 18,
                                fontWeight: 'bold',
                                color: selectedTab === tab.key ? tab.color : colors.textMuted,
                                marginBottom: 2
                            }}>
                                {tab.count}
                            </Text>
                            <Text style={{
                                fontSize: 10,
                                color: selectedTab === tab.key ? tab.color : colors.textMuted,
                                fontWeight: '600'
                            }}>
                                {tab.label}
                            </Text>
                        </TouchableOpacity>
                    ))}
                </View>

                {/* Tab Content */}
                <View style={{
                    backgroundColor: 'rgba(0,0,0,0.02)',
                    padding: 12,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: 'rgba(0,0,0,0.06)',
                    minHeight: 120,
                    maxHeight: 180
                }}>
                    {(!rsvpData || Object.keys(rsvpData).length === 0) && (!sharedContacts || sharedContacts.length === 0) ? (
                        <View style={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingVertical: 20
                        }}>
                            <Ionicons 
                                name="refresh"
                                size={20}
                                color={colors.textMuted}
                                style={{ opacity: 0.5, marginBottom: 6 }}
                            />
                            <Text style={{
                                fontSize: 12,
                                color: colors.textMuted
                            }}>
                                Loading...
                            </Text>
                        </View>
                    ) : usersByStatus[selectedTab] && usersByStatus[selectedTab].length > 0 ? (
                        <ScrollView style={{ maxHeight: 150 }}>
                            {(() => {
                                // Create maps for RSVP status lookup
                                const rsvpStatusMap = {};
                                ['going', 'maybe', 'decline'].forEach(status => {
                                    usersByStatus[status].forEach(user => {
                                        if (user.email) {
                                            rsvpStatusMap[user.email] = status === 'decline' ? 'Not Going' : status.charAt(0).toUpperCase() + status.slice(1);
                                        }
                                    });
                                });
                                
                                return usersByStatus[selectedTab].map((user, index) => {
                                    const isInvited = selectedTab === 'invited';
                                    const contactInfo = isInvited ? formatContactInfo(user) : null;
                                    const name = isInvited ? contactInfo.name : formatUserNameSync(user);
                                    const contactMethod = isInvited ? contactInfo.contactMethod : user.email;
                                    const channelLabel = isInvited ? contactInfo.channelLabel : null;
                                    const userPhone = !isInvited ? (user.phone || null) : null;
                                    
                                    // Get RSVP status for invited users
                                    const rsvpStatus = isInvited ? rsvpStatusMap[user.email] : null;
                                    
                                    return (
                                        <View key={`${selectedTab}-${index}-${user.uid || user.email || index}`} style={{
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            backgroundColor: 'white',
                                            padding: 8,
                                            borderRadius: 8,
                                            marginBottom: 6,
                                            borderLeftWidth: 2,
                                            borderLeftColor: tabConfig.find(t => t.key === selectedTab)?.color || '#dee2e6'
                                        }}>
                                            {/* Profile Photo */}
                                            <Image 
                                                source={getUserProfilePicture(user)}
                                                style={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: 14,
                                                    marginRight: 8
                                                }}
                                            />
                                            
                                            {/* User Info */}
                                            <View style={{ flex: 1 }}>
                                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                                    <Text style={{
                                                        fontWeight: '600',
                                                        color: colors.text,
                                                        fontSize: 12
                                                    }} numberOfLines={1}>
                                                        {name}
                                                    </Text>
                                                    {channelLabel && (
                                                        <View style={{
                                                            backgroundColor: channelLabel === 'SMS' ? '#fef3c7' : '#dbeafe',
                                                            paddingHorizontal: 6,
                                                            paddingVertical: 2,
                                                            borderRadius: 6
                                                        }}>
                                                            <Text style={{
                                                                fontSize: 9,
                                                                fontWeight: '700',
                                                                color: channelLabel === 'SMS' ? '#b45309' : '#2563eb'
                                                            }}>
                                                                {channelLabel === 'SMS' ? '💬 SMS' : '✉ Email'}
                                                            </Text>
                                                        </View>
                                                    )}
                                                    {userPhone && (
                                                        <View style={{
                                                            backgroundColor: '#fef3c7',
                                                            paddingHorizontal: 6,
                                                            paddingVertical: 2,
                                                            borderRadius: 6
                                                        }}>
                                                            <Text style={{
                                                                fontSize: 9,
                                                                fontWeight: '700',
                                                                color: '#b45309'
                                                            }}>
                                                                SMS
                                                            </Text>
                                                        </View>
                                                    )}
                                                </View>
                                                {contactMethod ? (
                                                    <Text style={{
                                                        fontSize: 10,
                                                        color: colors.textMuted
                                                    }} numberOfLines={1}>
                                                        {contactMethod}
                                                    </Text>
                                                ) : null}
                                                {userPhone ? (
                                                    <Text style={{
                                                        fontSize: 10,
                                                        color: '#4c956c',
                                                    }} numberOfLines={1}>
                                                        {userPhone}
                                                    </Text>
                                                ) : null}
                                            </View>

                                            {/* Right-aligned tags: unsubscribed + RSVP status */}
                                            {isInvited && (
                                                <View style={{ marginLeft: 8, alignItems: 'flex-end', justifyContent: 'center' }}>
                                                    <View style={{ flexDirection: 'row', gap: 8 }}>
                                                        {user.isUnsubscribed ? (
                                                            <View style={{ backgroundColor: 'rgba(220,53,69,0.08)', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8 }}>
                                                                <Text style={{ fontSize: 10, color: '#dc3545', fontWeight: '700' }}>Unsubscribed</Text>
                                                            </View>
                                                        ) : rsvpStatus ? (
                                                            <View style={{ backgroundColor: 'rgba(76,149,108,0.09)', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8 }}>
                                                                <Text style={{ fontSize: 10, color: '#2e7d32', fontWeight: '700' }}>{rsvpStatus}</Text>
                                                            </View>
                                                        ) : null}

                                                        {((user.status && user.status !== 'Invited') || (user.rsvpStatus && user.rsvpStatus !== 'Invited')) && (
                                                            <View style={{ backgroundColor: 'rgba(76,149,108,0.09)', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8 }}>
                                                                <Text style={{ fontSize: 10, color: '#2e7d32', fontWeight: '700' }}>
                                                                    RSVP: {user.status || user.rsvpStatus}
                                                                </Text>
                                                            </View>
                                                        )}
                                                    </View>
                                                </View>
                                            )}
                                        </View>
                                    );
                                });
                            })()}
                        </ScrollView>
                    ) : (
                        <View style={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingVertical: 20
                        }}>
                            <Ionicons 
                                name={tabConfig.find(t => t.key === selectedTab)?.iconName || 'document'}
                                size={28}
                                color={colors.textMuted}
                                style={{ opacity: 0.4, marginBottom: 8 }}
                            />
                            <Text style={{
                                fontSize: 13,
                                color: colors.textMuted,
                                fontWeight: '500',
                                textAlign: 'center'
                            }}>
                                No {tabConfig.find(t => t.key === selectedTab)?.label.toLowerCase()} yet
                            </Text>
                        </View>
                    )}
                </View>
                </View>
            )}

            {/* Global Reminder Settings - Horizontal Layout */}
            <View>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <Text style={{
                        fontSize: isMobile ? 14 : 15,
                        fontWeight: '600',
                        color: colors.text
                    }}>
                        Reminder Settings
                    </Text>
                    <Text style={{
                        fontSize: 11,
                        color: colors.textMuted
                    }}>
                        Syncs with RSVP widget
                    </Text>
                </View>
                
                {/* Reminder options - horizontal layout */}
                <View style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 10,
                    marginBottom: 12
                }}>
                    {globalReminderOptions.map((option) => {
                        const isSelected = reminderSettings.reminderTimes?.includes('day_of') && 
                                         reminderSettings.reminderTimes?.includes(option.value);
                        
                        return (
                            <TouchableOpacity
                                key={option.value}
                                style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    backgroundColor: isSelected ? 'rgba(76, 149, 108, 0.15)' : 'rgba(0,0,0,0.03)',
                                    paddingVertical: 10,
                                    paddingHorizontal: 14,
                                    borderRadius: 10,
                                    borderWidth: 1.5,
                                    borderColor: isSelected ? colors.primary : 'rgba(0,0,0,0.08)',
                                    gap: 8
                                }}
                                onPress={() => handleGlobalReminderToggle(option.value)}
                            >
                                <Ionicons 
                                    name={isSelected ? 'checkmark-circle' : 'ellipse-outline'}
                                    size={16}
                                    color={isSelected ? colors.primary : colors.textMuted}
                                />
                                <Text style={{
                                    fontSize: 12,
                                    color: isSelected ? colors.primary : colors.textMuted,
                                    fontWeight: isSelected ? '600' : '500'
                                }}>
                                    {option.shortLabel}
                                </Text>
                            </TouchableOpacity>
                        );
                    })}
                </View>

                {isAdminUser && (
                    <View style={{ marginTop: 8, marginBottom: 12, borderTopWidth: 1, borderTopColor: 'rgba(0,0,0,0.06)', paddingTop: 10 }}>
                        <Text style={{ fontSize: 11, fontWeight: '700', color: '#b45309', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Admin Testing</Text>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                            {testReminderOptions.map((option) => (
                                <TouchableOpacity
                                    key={option.value}
                                    style={{
                                        backgroundColor: '#fffbeb',
                                        paddingVertical: 8,
                                        paddingHorizontal: 14,
                                        borderRadius: 10,
                                        borderWidth: 1,
                                        borderColor: '#fbbf24',
                                    }}
                                    onPress={() => scheduleTestReminder(option.testDelay)}
                                >
                                    <Text style={{ fontSize: 12, color: '#b45309', fontWeight: '600' }}>{option.label}</Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                    </View>
                )}

                {/* Save Settings Button with Hover */}
                <Animated.View style={{ transform: [{ scale: saveButtonScale }] }}>
                    <TouchableOpacity
                        style={{
                            backgroundColor: saveSettingsHovered ? '#3d7a58' : colors.primary,
                            paddingVertical: 12,
                            borderRadius: 10,
                            alignItems: 'center',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            gap: 6,
                            shadowColor: colors.primary,
                            shadowOffset: { width: 0, height: saveSettingsHovered ? 6 : 3 },
                            shadowOpacity: saveSettingsHovered ? 0.4 : 0.25,
                            shadowRadius: saveSettingsHovered ? 10 : 6,
                            ...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {})
                        }}
                        onPress={saveReminderSettings}
                        onMouseEnter={() => {
                            setSaveSettingsHovered(true);
                            handleButtonHover(saveButtonScale, true);
                        }}
                        onMouseLeave={() => {
                            setSaveSettingsHovered(false);
                            handleButtonHover(saveButtonScale, false);
                        }}
                        activeOpacity={0.9}
                    >
                        <Ionicons name="save-outline" size={16} color="#fff" />
                        <Text style={{
                            color: 'white',
                            fontSize: 13,
                            fontWeight: '600'
                        }}>
                            Save Settings
                        </Text>
                    </TouchableOpacity>
                </Animated.View>
            </View>

            {/* Edit Event Modal */}
            <Modal
                visible={showEditModal}
                transparent
                animationType="fade"
                onRequestClose={() => {
                    setShowEditModal(false);
                    setEditingEvent(null);
                }}
            >
                <View style={{
                    flex: 1,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: isMobile ? 16 : 20
                }}>
                    <View style={{
                        backgroundColor: colors.creamLight,
                        borderRadius: 20,
                        padding: isMobile ? 20 : 28,
                        width: isMobile ? '95%' : '90%',
                        maxWidth: 480,
                        maxHeight: '85%',
                        overflow: 'hidden'
                    }}>
                        <ScrollView showsVerticalScrollIndicator={false}>
                            {/* Modal Header */}
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                                <Text style={{ fontSize: 22, fontWeight: '700', color: colors.text, fontFamily: 'Bodoni72Smallcaps' }}>
                                    {editingEvent?.isNew ? 'Add Event' : 'Edit Event'}
                                </Text>
                                <TouchableOpacity onPress={() => { setShowEditModal(false); setEditingEvent(null); }}>
                                    <Ionicons name="close" size={24} color={colors.textMuted} />
                                </TouchableOpacity>
                            </View>

                            {editingEvent && (
                                <View style={{ gap: 16 }}>
                                    {/* Event Title */}
                                    <View>
                                        <Text style={{ fontSize: 13, fontWeight: '600', color: colors.textMuted, marginBottom: 6 }}>
                                            Event Title *
                                        </Text>
                                        <TextInput
                                            style={{
                                                backgroundColor: colors.white,
                                                borderRadius: 10,
                                                padding: 14,
                                                fontSize: 15,
                                                color: colors.text,
                                                borderWidth: 1,
                                                borderColor: colors.border
                                            }}
                                            value={editingEvent.title}
                                            onChangeText={(text) => setEditingEvent(prev => ({ ...prev, title: text }))}
                                            placeholder="e.g., Graduation Ceremony"
                                            placeholderTextColor={colors.textMuted}
                                        />
                                    </View>

                                    {/* Date & Time Row */}
                                    <View style={{ flexDirection: 'column', gap: 12 }}>
                                        <View style={{ flex: 1 }}>
                                            <Text style={{ fontSize: 13, fontWeight: '600', color: colors.textMuted, marginBottom: 6 }}>
                                                Date *
                                            </Text>
                                            {Platform.OS === 'web' ? (
                                                <input
                                                    type="date"
                                                    value={editingEvent.date || ''}
                                                    onChange={(e) => setEditingEvent(prev => ({ ...prev, date: e.target.value }))}
                                                    style={{
                                                        backgroundColor: colors.white,
                                                        borderRadius: 10,
                                                        padding: 12,
                                                        fontSize: 15,
                                                        color: colors.text,
                                                        border: `1px solid ${colors.border}`,
                                                        width: '100%',
                                                        boxSizing: 'border-box',
                                                        fontFamily: 'inherit'
                                                    }}
                                                />
                                            ) : (
                                                <TextInput
                                                    style={{
                                                        backgroundColor: colors.white,
                                                        borderRadius: 10,
                                                        padding: 12,
                                                        fontSize: 15,
                                                        color: colors.text,
                                                        borderWidth: 1,
                                                        borderColor: colors.border
                                                    }}
                                                    value={editingEvent.date}
                                                    onChangeText={(text) => setEditingEvent(prev => ({ ...prev, date: text }))}
                                                    placeholder="YYYY-MM-DD"
                                                    placeholderTextColor={colors.textMuted}
                                                />
                                            )}
                                        </View>
                                        <View style={{ flex: 1 }}>
                                            <Text style={{ fontSize: 13, fontWeight: '600', color: colors.textMuted, marginBottom: 6 }}>
                                                Time *
                                            </Text>
                                            {Platform.OS === 'web' ? (
                                                <input
                                                    type="time"
                                                    value={editingEvent.time || ''}
                                                    onChange={(e) => setEditingEvent(prev => ({ ...prev, time: e.target.value }))}
                                                    style={{
                                                        backgroundColor: colors.white,
                                                        borderRadius: 10,
                                                        padding: 12,
                                                        fontSize: 15,
                                                        color: colors.text,
                                                        border: `1px solid ${colors.border}`,
                                                        width: '100%',
                                                        boxSizing: 'border-box',
                                                        fontFamily: 'inherit'
                                                    }}
                                                />
                                            ) : (
                                                <TextInput
                                                    style={{
                                                        backgroundColor: colors.white,
                                                        borderRadius: 10,
                                                        padding: 12,
                                                        fontSize: 15,
                                                        color: colors.text,
                                                        borderWidth: 1,
                                                        borderColor: colors.border
                                                    }}
                                                    value={editingEvent.time}
                                                    onChangeText={(text) => setEditingEvent(prev => ({ ...prev, time: text }))}
                                                    placeholder="HH:MM"
                                                    placeholderTextColor={colors.textMuted}
                                                />
                                            )}
                                        </View>
                                    </View>

                                    {/* Location */}
                                    <View>
                                        <Text style={{ fontSize: 13, fontWeight: '600', color: colors.textMuted, marginBottom: 6 }}>
                                            Location
                                        </Text>
                                        <TextInput
                                            style={{
                                                backgroundColor: colors.white,
                                                borderRadius: 10,
                                                padding: 14,
                                                fontSize: 15,
                                                color: colors.text,
                                                borderWidth: 1,
                                                borderColor: colors.border
                                            }}
                                            value={editingEvent.location || ''}
                                            onChangeText={(text) => setEditingEvent(prev => ({ ...prev, location: text }))}
                                            placeholder="e.g., University Auditorium"
                                            placeholderTextColor={colors.textMuted}
                                        />
                                    </View>

                                    {/* Description */}
                                    <View>
                                        <Text style={{ fontSize: 13, fontWeight: '600', color: colors.textMuted, marginBottom: 6 }}>
                                            Description
                                        </Text>
                                        <TextInput
                                            style={{
                                                backgroundColor: colors.white,
                                                borderRadius: 10,
                                                padding: 14,
                                                fontSize: 15,
                                                color: colors.text,
                                                borderWidth: 1,
                                                borderColor: colors.border,
                                                minHeight: 80,
                                                textAlignVertical: 'top'
                                            }}
                                            value={editingEvent.description || ''}
                                            onChangeText={(text) => setEditingEvent(prev => ({ ...prev, description: text }))}
                                            placeholder="Add details about this event..."
                                            placeholderTextColor={colors.textMuted}
                                            multiline
                                            numberOfLines={3}
                                        />
                                    </View>

                                    {/* Action Buttons */}
                                    <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
                                        {!editingEvent?.isNew && (
                                            <TouchableOpacity
                                                onPress={handleDeleteEvent}
                                                disabled={isSaving}
                                                style={{
                                                    flex: 1,
                                                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                                                    paddingVertical: 14,
                                                    borderRadius: 12,
                                                    alignItems: 'center',
                                                    flexDirection: 'row',
                                                    justifyContent: 'center',
                                                    gap: 6
                                                }}
                                            >
                                                <Ionicons name="trash-outline" size={18} color={colors.danger} />
                                                <Text style={{ fontSize: 15, fontWeight: '600', color: colors.danger }}>Delete</Text>
                                            </TouchableOpacity>
                                        )}
                                        <TouchableOpacity
                                            onPress={() => { setShowEditModal(false); setEditingEvent(null); }}
                                            disabled={isSaving}
                                            style={{
                                                flex: 1,
                                                backgroundColor: '#f0f0f0',
                                                paddingVertical: 14,
                                                borderRadius: 12,
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Text style={{ fontSize: 15, fontWeight: '600', color: colors.text }}>Cancel</Text>
                                        </TouchableOpacity>
                                        <TouchableOpacity
                                            onPress={handleSaveEvent}
                                            disabled={isSaving || !editingEvent?.title?.trim()}
                                            style={{
                                                flex: 1,
                                                backgroundColor: (!editingEvent?.title?.trim() || isSaving) ? '#ccc' : colors.primary,
                                                paddingVertical: 14,
                                                borderRadius: 12,
                                                alignItems: 'center',
                                                flexDirection: 'row',
                                                justifyContent: 'center',
                                                gap: 6
                                            }}
                                        >
                                            {isSaving ? (
                                                <Text style={{ fontSize: 15, fontWeight: '600', color: colors.white }}>Saving...</Text>
                                            ) : (
                                                <>
                                                    <Ionicons name="checkmark" size={18} color={colors.white} />
                                                    <Text style={{ fontSize: 15, fontWeight: '600', color: colors.white }}>Save</Text>
                                                </>
                                            )}
                                        </TouchableOpacity>
                                    </View>
                                </View>
                            )}
                        </ScrollView>
                    </View>
                </View>
            </Modal>

        </Animated.View>
    );
};

export default EventManagement;